/**
 * Fantasy Console 0 - Editor Tab State Context
 *
 * Persists editor tab state (selections, tool, camera, etc.) across
 * mount/unmount cycles when switching tabs with conditional rendering.
 */

import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
  type MutableRefObject,
} from 'react'

interface EditorTabStateContextType {
  store: MutableRefObject<Map<string, unknown>>
}

const EditorTabStateContext = createContext<EditorTabStateContextType | null>(
  null,
)

export function EditorTabStateProvider({ children }: { children: ReactNode }) {
  const store = useRef<Map<string, unknown>>(new Map())

  return (
    <EditorTabStateContext.Provider value={{ store }}>
      {children}
    </EditorTabStateContext.Provider>
  )
}

function useStore(): MutableRefObject<Map<string, unknown>> {
  const context = useContext(EditorTabStateContext)
  if (!context) {
    throw new Error(
      'usePersistedTabState must be used within an EditorTabStateProvider',
    )
  }
  return context.store
}

/**
 * Like useState, but persists the value in a shared store across unmount/remount cycles.
 * On mount: initializes from store (or defaultValue).
 * On unmount: saves current value back to store.
 */
export function usePersistedTabState<T>(
  key: string,
  defaultValue: T,
): [T, Dispatch<SetStateAction<T>>] {
  const store = useStore()

  const [value, setValue] = useState<T>(() => {
    if (store.current.has(key)) {
      return store.current.get(key) as T
    }
    return defaultValue
  })

  // Keep a ref so the cleanup always writes the latest value
  const valueRef = useRef(value)
  valueRef.current = value

  useEffect(() => {
    return () => {
      store.current.set(key, valueRef.current)
    }
  }, [key, store])

  return [value, setValue]
}

/**
 * Like useRef, but persists the ref value in a shared store across unmount/remount cycles.
 * On mount: initializes from store (or defaultValue).
 * On unmount: saves current ref value back to store.
 */
export function usePersistedRef<T>(
  key: string,
  defaultValue: T,
): MutableRefObject<T> {
  const store = useStore()

  const initialValue = store.current.has(key)
    ? (store.current.get(key) as T)
    : defaultValue

  const ref = useRef<T>(initialValue)

  useEffect(() => {
    return () => {
      store.current.set(key, ref.current)
    }
  }, [key, store])

  return ref
}
